import React from 'react';
import { Trans, i18nMark, withI18n } from '@lingui/react';
import { Form, Field } from 'react-final-form';

import InputField from '../../../../components/form/InputField';
import SwitchField from '../../../../components/form/SwitchField';
import ProfileRulesEditor from './ProfileRulesEditor';
import { validateProfile } from '../../validation/profileValidation';
import { useProfileForm, getProfileInitialValues } from '../../hooks/useProfileForm';

const ProfileEditModal = ({ profile, onClose, onSuccess, i18n }) => {
    const {
        isCreating,
        isSubmitting,
        isDeleting,
        showDeleteConfirm,
        setShowDeleteConfirm,
        onSubmit,
        handleDeleteConfirm,
    } = useProfileForm(profile, i18n, onSuccess || onClose);

    let submit;

    return (
        <div className="modal active">
            <span className="modal-overlay" aria-label="Close" />

            {showDeleteConfirm && (
                <div className="modal active" style={{ zIndex: 1000 }}>
                    <span className="modal-overlay" onClick={() => setShowDeleteConfirm(false)} />
                    <div className="modal-container" style={{ maxWidth: '450px' }}>
                        <div className="modal-header">
                            <div className="modal-title h5">
                                <Trans id="profiles:confirm:delete:title" />
                                <span className="btn btn-clear float-right" onClick={() => setShowDeleteConfirm(false)} />
                            </div>
                        </div>
                        <div className="modal-body">
                            <p><Trans id="profiles:confirm:delete" /></p>
                            <p><strong>{profile.name}</strong> ({profile.slug})</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                className={`btn btn-error mr-2 ${isDeleting ? 'loading' : ''}`}
                                onClick={handleDeleteConfirm}
                                disabled={isDeleting}
                            >
                                <Trans id="confirm:delete" />
                            </button>
                            <button
                                className="btn"
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={isDeleting}
                            >
                                <Trans id="cancel" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="modal-container" style={{ minWidth: '80vw', minHeight: '80vh' }}>
                <div className="modal-header">
                    <div className="modal-title h5">
                        {isCreating
                            ? <Trans id="profiles:new" />
                            : <><Trans id="profiles:edit" /> "{profile.name}"</>
                        }
                        <span
                            className="btn btn-clear float-right"
                            aria-label="Close"
                            onClick={onClose}
                        />
                    </div>
                </div>
                <div className="modal-body" style={{ flexGrow: 1 }}>
                    <Form
                        onSubmit={onSubmit}
                        validate={validateProfile}
                        initialValues={getProfileInitialValues(profile)}
                        render={({ handleSubmit, form, submitError }) => {
                            submit = handleSubmit;
                            const formValues = form.getState().values;

                            return (
                                <form className="form-horizontal" onSubmit={handleSubmit}>
                                    <Field
                                        name="name"
                                        component={InputField}
                                        placeholder={i18nMark('name')}
                                        className="form-input input-md my-2"
                                        layout="horizontal"
                                        mandatory={true}
                                    />

                                    {!isCreating && (
                                        <div className="form-group">
                                            <div className="col-3 col-sm-12">
                                                <label className="form-label">
                                                    <Trans id="profiles:slug" />
                                                </label>
                                            </div>
                                            <div className="col-9 col-sm-12">
                                                <code className="text-sm">{profile.slug}</code>
                                                <p className="text-gray text-sm mt-1">
                                                    <Trans id="profiles:slug:help" />
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <Field
                                        name="enabled"
                                        type="checkbox"
                                        component={SwitchField}
                                        placeholder={i18nMark('profiles:enabled')}
                                        layout="horizontal"
                                    />

                                    <div className="divider" />

                                    <h5 className="mt-3 mb-2">
                                        <Trans id="profiles:rules:title" />
                                    </h5>
                                    <p className="text-gray mb-3 d-none">
                                        <Trans id="profiles:rules:description" />
                                    </p>

                                    <ProfileRulesEditor
                                        formValues={formValues}
                                        form={form}
                                        showParentNodes={true}
                                    />

                                    {submitError && (
                                        <div className="bg-error mt-2 p-2 text-center text-light">
                                            {submitError}
                                        </div>
                                    )}
                                </form>
                            );
                        }}
                    />
                </div>
                <div className="modal-footer text-right">
                    <button
                        className={`btn btn-primary ${isSubmitting ? 'loading' : ''}`}
                        onClick={() => submit()}
                        disabled={isSubmitting}
                    >
                        {isCreating ? <Trans id="confirm:create" /> : <Trans id="confirm:update" />}
                    </button>
                    {!isCreating && (
                        <button
                            className="btn btn-error ml-2"
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={isSubmitting}
                        >
                            <Trans id="confirm:delete" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default withI18n()(ProfileEditModal);
